import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  CardContent,
  Drawer,
  Input,
  Tooltip,
  Field,
  FieldLabel,
  FieldError,
  PhoneField,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui';
import { useAuthStore } from '../../../../store/auth';
import { UserAvatar } from '@/components/UserAvatar';
import { useCurrentAcademy } from '../../../../hooks/useDataStore';
import { Coach, hasRole, TierFeatureKey, optionalE164PhoneSchema } from '@court-sync/shared';
import { useManagementDashboardFeatureAccess } from '@/hooks/useFeatureAccess';
import { useSubscriptionCheck } from '../../../../hooks/useSubscriptionCheck';
import { useCoaches, useLessons, useCoachInviteStatus } from '../../../../hooks/useDataStore';
import { coachService } from '../../../../services/coach';
import { userService } from '../../../../services/user';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import {
  GraduationCap,
  Mail,
  CheckCircle2,
  Clock,
  XCircle,
  TrendingUp,
  UserCheck,
  CalendarDays,
  ArrowRightLeft,
  Plus,
  MoreVertical,
  Loader2,
} from 'lucide-react';
import { CoachAvailabilityModal } from '../../../../components/staff/CoachAvailabilityModal';
import { CoachHoursReport } from '@/components/staff/CoachHoursReport';
import { BulkCoachReplaceModal } from '@/components/staff/BulkCoachReplaceModal';
import { SendCoachInviteDialog } from '@/components/staff/SendCoachInviteDialog';
import { CoachSportQualificationsField } from '@/components/sports/CoachSportQualificationsField';
import { ACADEMY_PAGE_TOUR_STEP_IDS, COACH_CREATE_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { DrawerTourHelp } from '@/components/tour/DrawerTourHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { usePageTourReady } from '@/store/pageTourReady';
import type { OnboardingEmbeddedPageProps } from '@/components/onboarding/onboardingEmbeddedPage';
import {
  canAutoAddCoachWithProfile,
  shouldAutoAddOwnerAsCoach,
  shouldShowSelfCoachPrompt,
} from '@/lib/selfCoachPrompt';

type InviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

// Form validation schema
const coachSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50, 'First name too long'),
  lastName: z.string().min(1, 'Last name is required').max(50, 'Last name too long'),
  email: z.string().email('Invalid email address'),
  phone: optionalE164PhoneSchema,
  bio: z.string().optional(),
  specialties: z.array(z.string()).optional(),
  hourlyRate: z.any().transform(v => (v === '' || v === undefined || v === null || isNaN(Number(v)) ? undefined : Number(v))).optional(),
  isActive: z.boolean(),
});

type CoachFormData = z.infer<typeof coachSchema>;

type CoachPerformanceStats = {
  attendanceRate?: number;
  totalAssigned?: number;
  totalCoached?: number;
  reportedAbsences?: number;
};

const SPECIALTY_OPTIONS = [
  { value: 'juniors', label: 'Juniors' },
  { value: 'adults', label: 'Adults' },
  { value: 'beginners', label: 'Beginners' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'competitive', label: 'Competitive' },
  { value: 'doubles', label: 'Doubles' },
  { value: 'singles', label: 'Singles' },
];

function InviteStatusBadge({ status }: { status?: InviteStatus }) {
  if (!status) return null;
  const config = {
    pending: { icon: Clock, label: 'Invite sent', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' },
    accepted: { icon: CheckCircle2, label: 'Accepted', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
    expired: { icon: XCircle, label: 'Invite expired', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
    revoked: { icon: XCircle, label: 'Revoked', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  };
  const { icon: Icon, label, className } = config[status] ?? config.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

interface SelfCoachPromptDialogProps {
  open: boolean;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
  onYes: () => void;
  onSkip: () => void;
  yesClickedRef: React.MutableRefObject<boolean>;
}

function SelfCoachPromptDialog({
  open,
  userFirstName,
  userLastName,
  userEmail,
  onYes,
  onSkip,
  yesClickedRef,
}: SelfCoachPromptDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !yesClickedRef.current && onSkip()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Are you also a coach?</DialogTitle>
          <DialogDescription>
            Many academy owners also coach their own students. Add yourself now to manage your own lessons and schedule.
          </DialogDescription>
        </DialogHeader>
        
        <div className="my-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium block mb-1">Your information:</span>
            {userFirstName} {userLastName}
            <br />
            {userEmail}
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onSkip}>
            I&apos;ll add coaches manually
          </Button>
          <Button onClick={onYes}>
            Yes, add me as a coach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CoachManagement({ embedded = false }: OnboardingEmbeddedPageProps = {}) {
  const { user } = useAuthStore();
  const { currentAcademy } = useCurrentAcademy();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const staffTab = searchParams.get('tab') === 'hours' ? 'hours' : 'coaches';
  const setStaffTab = (t: 'coaches' | 'hours') => {
    const next = new URLSearchParams(searchParams);
    if (t === 'hours') next.set('tab', 'hours');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };
  const { hasValidSubscription, tooltipMessage } = useSubscriptionCheck();
  const { hasAccess: canSendCoachEmailInvites } = useManagementDashboardFeatureAccess(TierFeatureKey.COACH_INVITES);
  const { hasAccess: hasCoachPortalAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.COACH_PORTAL);

  const { coaches, isLoading: loading, createCoach, updateCoach, deleteCoach, refresh } = useCoaches(currentAcademy?.id);
  const { lessons: allLessonsForBulk } = useLessons(currentAcademy?.id);
  const {
    inviteStatusByCoach,
    setInviteStatus,
  } = useCoachInviteStatus(currentAcademy?.id, {
    enabled: coaches.length > 0 && canSendCoachEmailInvites,
  });
  const [performanceModalCoach, setPerformanceModalCoach] = useState<Coach | null>(null);
  const [performanceModalStats, setPerformanceModalStats] = useState<CoachPerformanceStats | null>(null);
  const [loadingPerformanceCoachId, setLoadingPerformanceCoachId] = useState<string | null>(null);

  const [showBulkReplace, setShowBulkReplace] = useState(false);
  const [bulkReplaceFromCoachId, setBulkReplaceFromCoachId] = useState<string | undefined>();

  const [submitting, setSubmitting] = useState(false);
  const [editingCoach, setEditingCoach] = useState<Coach | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [inviteModalCoach, setInviteModalCoach] = useState<Coach | null>(null);
  const [showSelfCoachPrompt, setShowSelfCoachPrompt] = useState(false);
  const [dismissedSelfCoachPrompt, setDismissedSelfCoachPrompt] = useState(false);
  const [availabilityModalCoachId, setAvailabilityModalCoachId] = useState<string | null>(null);
  const [coachPendingRemove, setCoachPendingRemove] = useState<Coach | null>(null);
  const [removingCoach, setRemovingCoach] = useState(false);
  const [autoSelfAdding, setAutoSelfAdding] = useState(false);
  const selfCoachYesClickedRef = useRef(false);
  const autoSelfAddAttemptedRef = useRef(false);
  const [drawerPanelOpen, setDrawerPanelOpen] = useState(false);
  const coachTourScheduledRef = useRef(false);
  const cancelCoachTourScheduleRef = useRef<(() => void) | null>(null);
  const { startPageTour } = usePageTourActions();

  const COACH_FORM_ID = 'coach-form';

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CoachFormData>({
    resolver: zodResolver(coachSchema),
    defaultValues: {
      isActive: true,
      specialties: [],
      hourlyRate: undefined,
    },
  });

  // Watch email field to detect self-add
  const emailValue = watch('email');
  const hourlyRateValue = watch('hourlyRate');
  const isSelfEmail = emailValue && user?.email && emailValue.toLowerCase().trim() === user.email.toLowerCase().trim();

  const generateUID = (first: string, last: string) => {
    if (!first || !last) return '';
    const timestamp = Date.now().toString().slice(-6);
    return `${first.toLowerCase()}${last.toLowerCase()}${timestamp}`;
  };

  /** Marks the self-coach prompt as addressed for this academy (skip or chose to self-add). */
  const persistSelfCoachPromptDismissed = useCallback(async () => {
    const academyId = currentAcademy?.id;
    if (!academyId) return;
    const existing = useAuthStore.getState().user?.preferences?.selfCoachPromptDismissed;
    const selfCoachPromptDismissed = {
      ...(existing || {}),
      [academyId]: true,
    };
    try {
      await userService.updatePreferences({ selfCoachPromptDismissed });
      // Optimistically update the store so the effect won't re-show the prompt.
      // Deliberately avoid refreshUser() here — re-fetching the full profile triggers
      // the [user, navigate] effect and can navigate away or flicker the page mid-flow.
      const cur = useAuthStore.getState().user;
      if (cur) {
        useAuthStore.setState({
          user: {
            ...cur,
            preferences: { ...cur.preferences, selfCoachPromptDismissed },
          },
        });
      }
    } catch (error) {
      console.error('Failed to persist self-coach prompt dismissal:', error);
    }
  }, [currentAcademy?.id]);

  const openNewCoachForm = useCallback(() => {
    setEditingCoach(null);
    reset({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      bio: '',
      specialties: [],
      hourlyRate: undefined,
      isActive: true,
    });
    setShowForm(true);
  }, [reset]);

  const openSelfCoachForm = useCallback(() => {
    setEditingCoach(null);
    reset({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      bio: '',
      specialties: [],
      hourlyRate: undefined,
      isActive: true,
    });
    setShowForm(true);
  }, [reset, user?.email, user?.firstName, user?.lastName, user?.phone]);

  const autoAddSelfAsCoach = useCallback(async () => {
    if (!currentAcademy?.id || !user?.email || autoSelfAddAttemptedRef.current) return;

    if (!canAutoAddCoachWithProfile(user)) {
      autoSelfAddAttemptedRef.current = true;
      openSelfCoachForm();
      return;
    }

    autoSelfAddAttemptedRef.current = true;
    setAutoSelfAdding(true);

    try {
      const response = await coachService.selfAdd(currentAcademy.id, {
        firstName: user.firstName!.trim(),
        lastName: user.lastName!.trim(),
        email: user.email,
        phone: user.phone || undefined,
      });

      if (response.success && response.data?.coach) {
        toast.success('You have been added as a coach!');
        setInviteStatus(response.data.coach.id, 'accepted');
        await useAuthStore.getState().refreshUser({ forceTokenRefresh: true });
        refresh();
        void persistSelfCoachPromptDismissed();
        return;
      }

      openSelfCoachForm();
      toast.error(response.error || 'Failed to add you as a coach');
    } catch (error: unknown) {
      console.error('Error auto-adding solo coach:', error);
      openSelfCoachForm();
      toast.error(error instanceof Error ? error.message : 'Failed to add you as a coach');
    } finally {
      setAutoSelfAdding(false);
    }
  }, [currentAcademy?.id, openSelfCoachForm, persistSelfCoachPromptDismissed, refresh, setInviteStatus, user]);

  useEffect(() => {
    if (!user || (!hasRole(user, 'academy_owner') && !hasRole(user, 'academy_admin'))) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Teaching owners (solo / owner-coach) are auto-added when the roster is empty.
  // Owner-managers see a prompt on first visit when coach portal access is available.
  useEffect(() => {
    if (!user?.uid || !currentAcademy?.id || loading || autoSelfAdding) return;

    if (coaches.length > 0) {
      setShowSelfCoachPrompt(false);
      return;
    }

    const isDismissedInDb = user?.preferences?.selfCoachPromptDismissed?.[currentAcademy.id] ?? false;
    const dismissed = isDismissedInDb || dismissedSelfCoachPrompt;
    const promptInput = {
      coachesCount: coaches.length,
      dismissed,
      operatingProfile: currentAcademy.operatingProfile,
    };

    if (shouldAutoAddOwnerAsCoach(promptInput)) {
      setShowSelfCoachPrompt(false);
      void autoAddSelfAsCoach();
      return;
    }

    if (!hasCoachPortalAccess) {
      setShowSelfCoachPrompt(false);
      return;
    }

    setShowSelfCoachPrompt(shouldShowSelfCoachPrompt(promptInput));
  }, [
    autoAddSelfAsCoach,
    autoSelfAdding,
    coaches.length,
    currentAcademy?.id,
    currentAcademy?.operatingProfile,
    dismissedSelfCoachPrompt,
    hasCoachPortalAccess,
    loading,
    user?.preferences?.selfCoachPromptDismissed,
    user?.uid,
  ]);

  const onSubmit = async (data: CoachFormData) => {
    if (!currentAcademy) {
      toast.error('No academy selected');
      return;
    }

    // Validate: prevent duplicate coach by email (case-insensitive)
    const normalizedEmail = data.email.trim().toLowerCase();
    const duplicateCoach = coaches.find(
      (c) => c.id !== editingCoach?.id && (c.email ?? '').trim().toLowerCase() === normalizedEmail
    );
    if (duplicateCoach) {
      toast.error(`A coach with email ${data.email} is already in your academy`);
      return;
    }

    try {
      setSubmitting(true);

      if (editingCoach) {
        const existingAcademyIds = editingCoach.academyIds || [];
        const updatedAcademyIds = existingAcademyIds.includes(currentAcademy.id)
          ? existingAcademyIds
          : [...existingAcademyIds, currentAcademy.id];

        const coachData = {
          academyIds: updatedAcademyIds,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone || undefined,
          bio: data.bio || undefined,
          specialties: data.specialties || undefined,
          hourlyRate: data.hourlyRate ?? undefined,
          isActive: data.isActive,
        };
        await updateCoach(editingCoach.id, coachData);
        toast.success('Coach updated successfully');
      } else {
        // Check if this is a self-add (user adding themselves as coach with portal access)
        if (isSelfEmail && hasCoachPortalAccess) {
          const coachData = {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phone: data.phone || undefined,
            bio: data.bio || undefined,
            specialties: data.specialties || undefined,
            hourlyRate: data.hourlyRate ?? undefined,
          };
          const response = await coachService.selfAdd(currentAcademy.id, coachData);
          if (response.success && response.data?.coach) {
            toast.success('You have been added as a coach!');
            handleCancelEdit();
            refresh();
            // Auto-add to inviteStatusByCoach as "accepted"
            setInviteStatus(response.data.coach.id, 'accepted');
            // Refresh user data to pick up the new coach role
            await useAuthStore.getState().refreshUser({ forceTokenRefresh: true });
          }
        } else {
          const generatedUID = generateUID(data.firstName, data.lastName);

          const coachData = {
            uid: generatedUID,
            academyIds: [currentAcademy.id],
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phone: data.phone || undefined,
            bio: data.bio || undefined,
            specialties: data.specialties || undefined,
            hourlyRate: data.hourlyRate ?? undefined,
            isActive: data.isActive,
          };
          const createdCoach = await createCoach(coachData);
          toast.success('Coach created successfully!');
          handleCancelEdit();
          if (createdCoach && canSendCoachEmailInvites) {
            setInviteModalCoach(createdCoach);
          }
          return;
        }
      }

      handleCancelEdit();
    } catch (error: any) {
      console.error('Error saving coach:', error);
      toast.error(error.message || 'Failed to save coach');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (coach: Coach) => {
    setEditingCoach(coach);
    setValue('firstName', coach.firstName || '');
    setValue('lastName', coach.lastName || '');
    setValue('email', coach.email || '');
    setValue('phone', coach.phone || '');
    setValue('bio', coach.bio || '');
    setValue('specialties', coach.specialties || []);
    setValue('hourlyRate', coach.hourlyRate);
    setValue('isActive', coach.isActive);
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setEditingCoach(null);
    setShowForm(false);
    reset({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      bio: '',
      specialties: [],
      hourlyRate: undefined,
      isActive: true,
    });
  };

  useEffect(() => {
    if (!showForm) {
      setDrawerPanelOpen(false);
      coachTourScheduledRef.current = false;
      cancelCoachTourScheduleRef.current?.();
      cancelCoachTourScheduleRef.current = null;
    }
  }, [showForm]);

  const handleCoachDrawerPanelOpen = useCallback(() => {
    setDrawerPanelOpen(true);
    if (!showForm || editingCoach || !hasValidSubscription || coachTourScheduledRef.current) {
      return;
    }
    coachTourScheduledRef.current = true;
    cancelCoachTourScheduleRef.current?.();
    cancelCoachTourScheduleRef.current = scheduleAutoPageTour(
      startPageTour,
      'academy-coaches-create',
      user?.preferences,
    );
  }, [editingCoach, hasValidSubscription, showForm, startPageTour, user?.preferences]);

  const handleSelfCoachPromptYes = () => {
    selfCoachYesClickedRef.current = true;
    setShowSelfCoachPrompt(false);
    setDismissedSelfCoachPrompt(true);
    openSelfCoachForm();
    void persistSelfCoachPromptDismissed();
  };

  const handleSelfCoachPromptSkip = () => {
    if (!user?.uid || !currentAcademy?.id) return;
    setShowSelfCoachPrompt(false);
    setDismissedSelfCoachPrompt(true);
    void persistSelfCoachPromptDismissed();
  };

  const handleConfirmRemoveCoach = async () => {
    const coach = coachPendingRemove;
    if (!coach) return;

    try {
      setRemovingCoach(true);
      await deleteCoach(coach.id);
      toast.success('Coach removed successfully');
      setCoachPendingRemove(null);
    } catch (error: any) {
      console.error('Error deleting coach:', error);
      toast.error(error.message || 'Failed to remove coach');
    } finally {
      setRemovingCoach(false);
    }
  };

  const handleOpenInviteModal = (coach: Coach) => {
    setInviteModalCoach(coach);
  };

  const getInviteButtonConfig = (coach: Coach) => {
    const status = inviteStatusByCoach[coach.id];
    if (status === 'accepted') return null;
    if (status === 'pending' || status === 'expired' || status === 'revoked') {
      return { label: 'Re-send invite', isResend: true };
    }
    return { label: 'Send invite', isResend: false };
  };

  const openCoachPerformanceModal = useCallback(
    async (coach: Coach) => {
      if (!currentAcademy?.id) return;
      setPerformanceModalCoach(coach);
      setPerformanceModalStats(null);
      setLoadingPerformanceCoachId(coach.id);
      try {
        const stats = await coachService.getCoachStats(coach.id, currentAcademy.id);
        setPerformanceModalStats(stats);
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Failed to load performance');
        setPerformanceModalCoach(null);
      } finally {
        setLoadingPerformanceCoachId(null);
      }
    },
    [currentAcademy?.id]
  );

  const filteredCoaches = coaches;

  usePageTourReady(
    'academy-coaches-create',
    showForm && !editingCoach && drawerPanelOpen && hasValidSubscription,
  );

  if (!user || (!hasRole(user, 'academy_owner') && !hasRole(user, 'academy_admin'))) {
    return null;
  }

  // Academy data may still be loading from preload

  if (!currentAcademy) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className={embedded ? 'min-h-0' : 'min-h-screen'}>
      <div className="contents">
        {!embedded ? (
          <PageHeader
            title="Coaches"
            help={
              <PageFeatureHelp title="Coaches">
                <p>Manage your coaching staff and optional portal access.</p>
              </PageFeatureHelp>
            }
            actions={
              staffTab === 'coaches' && !loading && filteredCoaches.length > 0 ? (
                <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                  <Button
                    id={ACADEMY_PAGE_TOUR_STEP_IDS.COACHES_NEW}
                    type="button"
                    size="sm"
                    className="w-full md:w-auto"
                    onClick={() => openNewCoachForm()}
                    disabled={!hasValidSubscription}
                  >
                    <Plus aria-hidden />
                    Add Coach
                  </Button>
                </Tooltip>
              ) : undefined
            }
          />
        ) : null}

        <div id={ACADEMY_PAGE_TOUR_STEP_IDS.COACHES_TABS}>
        <DashboardTabNav<'coaches' | 'hours'>
          tabs={[
            { id: 'coaches', label: 'Coaches' },
            { id: 'hours', label: 'Hours' },
          ]}
          activeTab={staffTab}
          onChange={setStaffTab}
          ariaLabel="Staff sections"
        />
        </div>

        {staffTab === 'hours' && currentAcademy?.id ? (
          <CoachHoursReport academyId={currentAcademy.id} coaches={coaches} />
        ) : null}

        {staffTab === 'coaches' && (
          <>
        {currentAcademy.privateBookingEnabled &&
          coaches.some((c) => c.isActive && (c.hourlyRate == null || c.hourlyRate <= 0)) && (
            <div
              className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
              role="status"
            >
              <strong>Private lesson booking is on.</strong> Set an hourly rate for each active coach so they appear in
              the private booking flow.
            </div>
          )}

        {hasCoachPortalAccess ? (
          <SelfCoachPromptDialog
            open={showSelfCoachPrompt}
            userFirstName={user?.firstName}
            userLastName={user?.lastName}
            userEmail={user?.email}
            onYes={handleSelfCoachPromptYes}
            onSkip={handleSelfCoachPromptSkip}
            yesClickedRef={selfCoachYesClickedRef}
          />
        ) : null}

        <Drawer
          isOpen={showForm}
          onClose={handleCancelEdit}
          title={editingCoach ? 'Edit Coach' : 'Add New Coach'}
          width="lg"
          panelId={!editingCoach ? COACH_CREATE_TOUR_STEP_IDS.DRAWER : undefined}
          onPanelOpen={!editingCoach ? handleCoachDrawerPanelOpen : undefined}
          headerActions={
            !editingCoach && hasValidSubscription ? (
              <DrawerTourHelp
                tourKey="academy-coaches-create"
                suggestion="Adding a coach? Tour this form — pay special attention to email and hourly rate."
                buttonLabel="Tour this form"
              />
            ) : undefined
          }
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                <Button
                  id={!editingCoach ? COACH_CREATE_TOUR_STEP_IDS.SUBMIT : undefined}
                  type="submit"
                  form={COACH_FORM_ID}
                  disabled={submitting || !hasValidSubscription}
                >
                  {submitting ? 'Saving...' : editingCoach ? 'Update Coach' : 'Add Coach'}
                </Button>
              </Tooltip>
            </div>
          }
        >
              <form id={COACH_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div
                  id={!editingCoach ? COACH_CREATE_TOUR_STEP_IDS.CONTACT : undefined}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <Field data-invalid={!!errors.firstName?.message}>
                    <FieldLabel htmlFor="firstName">First Name *</FieldLabel>
                    <Input id="firstName" aria-invalid={!!errors.firstName?.message} {...register('firstName')} placeholder="John" />
                    <FieldError>{errors.firstName?.message}</FieldError>
                  </Field>

                  <Field data-invalid={!!errors.lastName?.message}>
                    <FieldLabel htmlFor="lastName">Last Name *</FieldLabel>
                    <Input id="lastName" aria-invalid={!!errors.lastName?.message} {...register('lastName')} placeholder="Doe" />
                    <FieldError>{errors.lastName?.message}</FieldError>
                  </Field>

                  <Field data-invalid={!!errors.email?.message}>
                    <FieldLabel htmlFor="email">Email *</FieldLabel>
                    <Input id="email" type="email" aria-invalid={!!errors.email?.message} {...register('email')} placeholder="john.doe@example.com" />
                    <FieldError>{errors.email?.message}</FieldError>
                    {isSelfEmail && hasCoachPortalAccess && (
                      <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800 flex items-start gap-2">
                        <UserCheck className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          This is your email. You&apos;ll be added directly as a coach — no invite needed.
                        </p>
                      </div>
                    )}
                  </Field>

                  <PhoneField
                    control={control}
                    name="phone"
                    label="Phone"
                    id="phone"
                    purpose="coach_contact"
                    defaultCountrySource={{
                      country: currentAcademy?.country,
                      region: currentAcademy?.region,
                    }}
                    placeholder="Phone number"
                  />
                </div>

                <div
                  id={!editingCoach ? COACH_CREATE_TOUR_STEP_IDS.COMPENSATION : undefined}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <Field data-invalid={!!errors.hourlyRate?.message}>
                    <FieldLabel htmlFor="hourlyRate">Hourly Rate ($)</FieldLabel>
                    <Input id="hourlyRate" type="number" aria-invalid={!!errors.hourlyRate?.message} {...register('hourlyRate')} placeholder="75" />
                    <FieldError>{errors.hourlyRate?.message}</FieldError>
                    {currentAcademy.privateBookingEnabled &&
                      (() => {
                        const n = Number(hourlyRateValue);
                        return !Number.isFinite(n) || n <= 0;
                      })() && (
                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-1.5">
                          Required for private lesson booking — students are charged from this rate.
                        </p>
                      )}
                  </Field>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <select
                      {...register('isActive', {
                        setValueAs: (v) => v === true || v === 'true',
                      })}
                      className="input"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>

                <div
                  id={!editingCoach ? COACH_CREATE_TOUR_STEP_IDS.PROFILE : undefined}
                  className="space-y-6"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Bio
                    </label>
                    <textarea
                      {...register('bio')}
                      rows={3}
                      className="input"
                      placeholder="Tell us about this coach's experience and background..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Specialties
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {SPECIALTY_OPTIONS.map((specialty) => (
                        <label key={specialty.value} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            value={specialty.value}
                            {...register('specialties')}
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {specialty.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {editingCoach && currentAcademy ? (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <CoachSportQualificationsField
                      academyId={currentAcademy.id}
                      coachId={editingCoach.id}
                      disabled={!hasValidSubscription || submitting}
                    />
                  </div>
                ) : null}

              </form>
        </Drawer>

        {/* Coaches List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          </div>
        ) : !loading && filteredCoaches.length === 0 ? (
          <DashboardEmptyStateCard>
            <DashboardEmptyStateIcon icon={GraduationCap} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No coaches found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {canSendCoachEmailInvites
                ? `You haven't added any coaches to ${currentAcademy?.name} yet. Start by adding coaches, then send them an invite to create their profile.`
                : `You haven't added any coaches to ${currentAcademy?.name} yet. Add coaches here to assign them to lessons and track availability.`}
            </p>
            <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
              <Button
                id={ACADEMY_PAGE_TOUR_STEP_IDS.COACHES_NEW}
                onClick={() => openNewCoachForm()}
                disabled={!hasValidSubscription}
              >
                Add Your First Coach
              </Button>
            </Tooltip>
          </DashboardEmptyStateCard>
        ) : !loading && filteredCoaches.length > 0 ? (
          <>
            <div
              id={ACADEMY_PAGE_TOUR_STEP_IDS.COACHES_LIST}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredCoaches.map((coach) => {
                const inviteButtonConfig = getInviteButtonConfig(coach);
                const inviteStatus = inviteStatusByCoach[coach.id];

                return (
                  <Card key={coach.id} className="relative flex flex-col h-full min-h-[20rem] hover:shadow-md transition-shadow">
                    <CardContent className="flex flex-col p-6 flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <UserAvatar
                            avatarUrl={coach.avatarUrl}
                            firstName={coach.firstName}
                            lastName={coach.lastName}
                            size="md"
                          />
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                              {coach.firstName} {coach.lastName}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                              {currentAcademy?.name}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${coach.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                            }`}>
                            {coach.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {canSendCoachEmailInvites ? <InviteStatusBadge status={inviteStatus} /> : null}
                        </div>
                      </div>

                      <div className="mb-4 space-y-1">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Email:</span> {coach.email}
                        </p>
                        {coach.phone && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Phone:</span> {coach.phone}
                          </p>
                        )}
                      </div>

                      {coach.bio && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                          {coach.bio}
                        </p>
                      )}

                      {coach.specialties && coach.specialties.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Specialties:
                          </h4>
                          <div className="flex flex-wrap gap-1">
                            {coach.specialties.map((specialty) => (
                              <span
                                key={specialty}
                                className="px-2 py-1 text-xs bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 rounded-full"
                              >
                                {specialty}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {coach.hourlyRate && (
                        <div className="mb-4">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Hourly Rate:</span> ${coach.hourlyRate}
                          </p>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 pt-4 mt-auto border-t border-gray-200 dark:border-gray-700">
                        <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(coach)}
                            disabled={!hasValidSubscription}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="inline-flex items-center gap-1"
                              type="button"
                              aria-label={`More actions for ${coach.firstName} ${coach.lastName}`}
                            >
                              More
                              <MoreVertical className="w-3.5 h-3.5" aria-hidden />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            {inviteButtonConfig && canSendCoachEmailInvites && (
                              <DropdownMenuItem
                                disabled={!hasValidSubscription}
                                onSelect={() => handleOpenInviteModal(coach)}
                              >
                                <Mail className="mr-2 h-4 w-4" />
                                {inviteButtonConfig.label}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onSelect={() => setAvailabilityModalCoachId(coach.id)}>
                              <CalendarDays className="mr-2 h-4 w-4" />
                              Availability
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={!hasValidSubscription}
                              onSelect={() => {
                                setBulkReplaceFromCoachId(coach.id);
                                setShowBulkReplace(true);
                              }}
                            >
                              <ArrowRightLeft className="mr-2 h-4 w-4" />
                              Cover dates
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() => void openCoachPerformanceModal(coach)}
                              disabled={loadingPerformanceCoachId === coach.id}
                            >
                              {loadingPerformanceCoachId === coach.id ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <TrendingUp className="mr-2 h-4 w-4" />
                              )}
                              View performance
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={!hasValidSubscription}
                              className="text-red-600 focus:text-red-600"
                              onSelect={() => setCoachPendingRemove(coach)}
                            >
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {availabilityModalCoachId && (() => {
              const coach = filteredCoaches.find((c) => c.id === availabilityModalCoachId);
              if (!coach || !currentAcademy) return null;
              return (
                <CoachAvailabilityModal
                  coachId={availabilityModalCoachId}
                  academyId={currentAcademy.id}
                  coachName={`${coach.firstName} ${coach.lastName}`}
                  isOpen={true}
                  onClose={() => setAvailabilityModalCoachId(null)}
                />
              );
            })()}
          </>
        ) : null}

        {/* Coach performance */}
        <Dialog
          open={!!performanceModalCoach}
          onOpenChange={(open) => {
            if (!open && !loadingPerformanceCoachId) {
              setPerformanceModalCoach(null);
              setPerformanceModalStats(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Coach performance
              </DialogTitle>
              <DialogDescription>
                {performanceModalCoach && (
                  <>
                    {performanceModalCoach.firstName} {performanceModalCoach.lastName} — sessions in
                    active terms, camps, and recent private lessons.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            {loadingPerformanceCoachId ? (
              <div className="flex items-center justify-center py-10" aria-busy="true">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : performanceModalStats ? (
              <dl className="space-y-3 rounded-lg bg-muted/50 p-4 text-sm">
                {performanceModalStats.attendanceRate !== undefined && (
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="font-medium text-foreground">Attendance rate</dt>
                    <dd className="text-muted-foreground tabular-nums">
                      {(performanceModalStats.attendanceRate || 0).toFixed(1)}%
                    </dd>
                  </div>
                )}
                {performanceModalStats.totalAssigned !== undefined && (
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="font-medium text-foreground">Sessions assigned</dt>
                    <dd className="text-muted-foreground tabular-nums">
                      {performanceModalStats.totalAssigned}
                    </dd>
                  </div>
                )}
                {performanceModalStats.reportedAbsences !== undefined && (
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="font-medium text-foreground">Reported absences</dt>
                    <dd className="text-muted-foreground tabular-nums">
                      {performanceModalStats.reportedAbsences}
                    </dd>
                  </div>
                )}
              </dl>
            ) : null}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setPerformanceModalCoach(null);
                  setPerformanceModalStats(null);
                }}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove coach confirmation */}
        <Dialog
          open={!!coachPendingRemove}
          onOpenChange={(open) => {
            if (!open && !removingCoach) {
              setCoachPendingRemove(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Remove coach?</DialogTitle>
              <DialogDescription>
                {coachPendingRemove && (
                  <>
                    Remove <strong>{coachPendingRemove.firstName} {coachPendingRemove.lastName}</strong> (
                    {coachPendingRemove.email}) from your coaching staff? This cannot be undone from here.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setCoachPendingRemove(null)} disabled={removingCoach}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleConfirmRemoveCoach()}
                disabled={removingCoach || !hasValidSubscription}
              >
                {removingCoach ? 'Removing…' : 'Remove coach'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {canSendCoachEmailInvites ? (
          <SendCoachInviteDialog
            coach={inviteModalCoach}
            isOpen={!!inviteModalCoach}
            onClose={() => setInviteModalCoach(null)}
            academyId={currentAcademy.id}
            onSent={() => {
              if (inviteModalCoach) {
                setInviteStatus(inviteModalCoach.id, 'pending');
              }
              refresh();
            }}
          />
        ) : null}
          </>
        )}

        <BulkCoachReplaceModal
          isOpen={showBulkReplace}
          onClose={() => {
            setShowBulkReplace(false);
            setBulkReplaceFromCoachId(undefined);
          }}
          academyId={currentAcademy.id}
          coaches={coaches}
          lessons={allLessonsForBulk}
          initialFromCoachId={bulkReplaceFromCoachId}
          onSuccess={() => void refresh()}
        />
      </div>
    </div>
  );
}
